import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import s from './ModelVariantThresholds.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialModelVariantState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import { isRequired } from '../../utils';
import Cookies from 'js-cookie';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import InputNumber from '../WSAControls/InputNumber/InputNumber';
import { compareValues } from '../../utils';
import Checkbox from '../WSAControls/CheckboxContainer/Checkbox';

const ModelThreshold = ({
  selectedModelId,
  selectedModelThresholdId,
  ModelCategoryId,
  selectedModelVariantId,
  saveModelThreshold,
  modelThreshold = { ...initialModelVariantState.modelVariantManager.selectedModelVariant.Thresholds },
  changeModelThreshold,
  modelComponentPositions,
  cancelHandler,
  defaultThresholdHandler,
  intl
}) => {
  let IsEdit = false;
  let IsRedirect = false;
  const [localformErrors, setFormErrors] = useState({});
  const [minRange, setMinRange] = useState(null);
  const [maxRange, setMaxRange] = useState(null);
  const [limitInfo, setLimitInfo] = useState('');
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let minRangeTransaltion = intl.formatMessage({ id: 'threshold.minThresholdRange', defaultMessage: 'Min Range' });
  let maxRangeTransaltion = intl.formatMessage({ id: 'threshold.maxThresholdRange', defaultMessage: 'Max Range' });
  let selectedSensorId = modelThreshold.SelectedThreshold.ModelComponentPositionId || 0;

  if (selectedModelThresholdId) {
    IsEdit = true;
  } else {
    IsRedirect = true;
  }

  let widgetCode = 'MODELVARIANTTHRESHOLDSEDIT';

  //clean up
  useEffect(() => {
    return () => {
      cancelHandler();
    };
  }, []);

  useEffect(() => {
    if (selectedSensorId) {
      setThresholdRangeIndicator(selectedSensorId);
    }
  }, [selectedSensorId]);

  // Update redux store
  const setLocalModelVariantThreshold = currentState => {
    changeModelThreshold(currentState);
  };

  //Saving
  const submitForm = e => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = modelThreshold.SelectedThreshold;
    saveData.PerformResetDefault = false;
    saveData.IsEdit = IsEdit;
    saveData.ModelCategoryId = ModelCategoryId;
    saveData.ModelVariantId = selectedModelVariantId;
    saveData.ModelId = selectedModelId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;

    if (!IsEdit) {
      saveData.selectedModelThresholdId = 0;
      saveData.GuidIdentifier = uuidv4();
    }

    saveModelThreshold(saveData, widgetCode);
  };


  const validateInput = () => {
    let formErrors = {};
    let isValid = true;

    if (
      !modelThreshold ||
      !modelThreshold.SelectedThreshold ||
      !modelThreshold.SelectedThreshold.SetpointId ||
      isRequired(modelThreshold.SelectedThreshold.SetpointId, 1)
    ) {
      formErrors.SetpointId = intl.formatMessage({
        id: 'modelThreshold.FlowSequenceMandotory',
        defaultMessage: 'Flow Sequence is a mandatory field'
      });
      isValid = false;
    }

    if (
      !modelThreshold ||
      !modelThreshold.SelectedThreshold ||
      !modelThreshold.SelectedThreshold.ModelComponentPositionId ||
      isRequired(modelThreshold.SelectedThreshold.ModelComponentPositionId, 1)
    ) {
      formErrors.ModelComponentPositionId = intl.formatMessage({
        id: 'threshold.sensorMandatory',
        defaultMessage: 'Sensor is a mandatory field'
      });
      isValid = false;
    }

    if (!hasValidTresholdRanges()) {
      formErrors.thresholdValidation = intl.formatMessage({
        id: 'threshold.thresholdRangeInvalid',
        defaultMessage: 'Invalid threshold range, please use the min and max ranges indicated'
      });
      isValid = false;
    }

    setFormErrors(formErrors);

    return isValid;
  };

  const hasValidTresholdRanges = () => {
    // Low Low value range validation
    if (modelThreshold.SelectedThreshold.Min) {
      if (
        (minRange && modelThreshold.SelectedThreshold.Min < minRange) ||
        (modelThreshold.SelectedThreshold.CriticalLow &&
          modelThreshold.SelectedThreshold.Min >= modelThreshold.SelectedThreshold.CriticalLow) ||
        (modelThreshold.SelectedThreshold.Low &&
          modelThreshold.SelectedThreshold.Min >= modelThreshold.SelectedThreshold.Low) ||
        (modelThreshold.SelectedThreshold.High &&
          modelThreshold.SelectedThreshold.Min >= modelThreshold.SelectedThreshold.High) ||
        (modelThreshold.SelectedThreshold.CriticalHigh &&
          modelThreshold.SelectedThreshold.Min >= modelThreshold.SelectedThreshold.CriticalHigh) ||
        (modelThreshold.SelectedThreshold.Max &&
          modelThreshold.SelectedThreshold.Min >= modelThreshold.SelectedThreshold.Max) ||
        (maxRange && modelThreshold.SelectedThreshold.Min > maxRange)
      ) {
        return false;
      }
    }

    // Low  value range validation
    if (modelThreshold.SelectedThreshold.CriticalLow) {
      if (
        (minRange && modelThreshold.SelectedThreshold.CriticalLow < minRange) ||
        (modelThreshold.SelectedThreshold.Low &&
          modelThreshold.SelectedThreshold.CriticalLow >= modelThreshold.SelectedThreshold.Low) ||
        (modelThreshold.SelectedThreshold.High &&
          modelThreshold.SelectedThreshold.CriticalLow >= modelThreshold.SelectedThreshold.High) ||
        (modelThreshold.SelectedThreshold.CriticalHigh &&
          modelThreshold.SelectedThreshold.CriticalLow >= modelThreshold.SelectedThreshold.CriticalHigh) ||
        (modelThreshold.SelectedThreshold.Max &&
          modelThreshold.SelectedThreshold.CriticalLow >= modelThreshold.SelectedThreshold.Max) ||
        (maxRange && modelThreshold.SelectedThreshold.CriticalLow > maxRange)
      ) {
        return false;
      }
    }
    // Op Low Validation
    if (modelThreshold.SelectedThreshold.Low) {
      if (
        (minRange && modelThreshold.SelectedThreshold.Low < minRange) ||
        (modelThreshold.SelectedThreshold.High &&
          modelThreshold.SelectedThreshold.Low >= modelThreshold.SelectedThreshold.High) ||
        (modelThreshold.SelectedThreshold.CriticalHigh &&
          modelThreshold.SelectedThreshold.Low >= modelThreshold.SelectedThreshold.CriticalHigh) ||
        (modelThreshold.SelectedThreshold.Max &&
          modelThreshold.SelectedThreshold.Low >= modelThreshold.SelectedThreshold.Max) ||
        (maxRange && modelThreshold.SelectedThreshold.Low > maxRange)
      ) {
        return false;
      }
    }

    // Op High Validation
    if (modelThreshold.SelectedThreshold.High) {
      if (
        (minRange && modelThreshold.SelectedThreshold.High < minRange) ||
        (modelThreshold.SelectedThreshold.CriticalHigh &&
          modelThreshold.SelectedThreshold.High >= modelThreshold.SelectedThreshold.CriticalHigh) ||
        (modelThreshold.SelectedThreshold.Max &&
          modelThreshold.SelectedThreshold.High >= modelThreshold.SelectedThreshold.Max) ||
        (maxRange && modelThreshold.SelectedThreshold.High > maxRange)
      ) {
        return false;
      }
    }

    // High Validation
    if (modelThreshold.SelectedThreshold.CriticalHigh) {
      if (
        (minRange && modelThreshold.SelectedThreshold.CriticalHigh < minRange) ||
        (modelThreshold.SelectedThreshold.Max &&
          modelThreshold.SelectedThreshold.CriticalHigh >= modelThreshold.SelectedThreshold.Max) ||
        (maxRange && modelThreshold.SelectedThreshold.CriticalHigh > maxRange)
      ) {
        return false;
      }
    }

    // High High Validation
    if (modelThreshold.SelectedThreshold.Max) {
      if (
        (minRange && modelThreshold.SelectedThreshold.Max < minRange) ||
        (maxRange && modelThreshold.SelectedThreshold.Max > maxRange)
      ) {
        return false;
      }
    }

    return true;
  };

  //on control value change
  const onChange = e => {
    if (e.target && e.target.type && e.target.type === 'checkbox') {
      setLocalModelVariantThreshold({
        SelectedThreshold: { ...modelThreshold.SelectedThreshold, [e.target.name]: e.target.checked }
      });
    } else {
      
      setLocalModelVariantThreshold({
        SelectedThreshold: { ...modelThreshold.SelectedThreshold, [e.target.name]: e.target.value === '' ? null : Number(e.target.value) }
      });
    }
  };


  const setThresholdRangeIndicator = id => {
    let limitMin = '';
    let limitMax = '';
    let limitSep = '';
    const sensorComponent = modelComponentPositions.find(item => item.ModelComponentPositionId === id);

    if (sensorComponent) {
      if (sensorComponent.MinThresholdLimit) {
        setMinRange(sensorComponent.MinThresholdLimit);
        limitMin = `${minRangeTransaltion} ${sensorComponent.MinThresholdLimit.toString()}`;
      } else {
        setMinRange(null);
      }
      if (sensorComponent.MaxThresholdLimit) {
        setMaxRange(sensorComponent.MaxThresholdLimit);
        limitMax = `${maxRangeTransaltion} ${sensorComponent.MaxThresholdLimit.toString()}`;
      } else {
        setMaxRange(null);
      }
      if (sensorComponent.MinThresholdLimit && sensorComponent.MaxThresholdLimit) limitSep = ', ';
    }
    setLimitInfo(`${limitMin} ${limitSep} ${limitMax}`);
  };

  return (
    <div className={s.modelThreshold}>
      <div className={`${s.contentWrapper} ${s.padding40}`}>
        <Form>
          <div className={s.topRow} data-unittest="formAddEditThresholds">
            <Row>
              <Col lg={10}>
                <Row>
                  <Col lg={4}>
                    <Form.Group controlId="formFlowSequence">
                      <Form.Label>
                        <FormattedMessage id="threshold.thresholdFlowSequence" defaultMessage="Flow Sequence" />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="FlowSequence"
                        placeholder={intl.formatMessage({
                          id: 'threshold.thresholdFlowSequence',
                          defaultMessage: 'Flow Sequence'
                        })}
                        value={modelThreshold.SelectedThreshold.FlowSequence || ''}
                        className={`${s.formControl}`}
                        disabled={true}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Form.Group controlId="formSensor">
                      <Form.Label>
                        <FormattedMessage id="threshold.thresholdSensor" defaultMessage="Sensor" />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="ModelComponentName"
                        placeholder={intl.formatMessage({
                          id: 'threshold.thresholdSensor',
                          defaultMessage: 'Sensor'
                        })}
                        value={modelThreshold.SelectedThreshold.ModelComponentName || ''}
                        className={`${s.formControl}`}
                        disabled={true}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={4} className={s.limitInfo}>
                    <span>{limitInfo}</span>
                  </Col>
                </Row>
              </Col>
              <Col lg={2}></Col>
            </Row>
            <Row>
              <Col lg={10}>
                <Row>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdLowLow">
                      <Form.Label className={s.red}>
                        <FormattedMessage id="threshold.thresholdLowLow" defaultMessage="Low Low" />
                      </Form.Label>
                      <InputNumber
                        name="Min"
                        step=".1"
                        customClassName={s.redBorder}
                        onInputChange={onChange}
                        value={modelThreshold.SelectedThreshold.Min?.toString() ?? ''}
                        id="formThresholdLowLowValue"
                      />
                      {localformErrors && localformErrors.Min && (
                        <p role="alert" className={s.error}>
                          {localformErrors.Min}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdLow">
                      <Form.Label className={s.orange}>
                        <FormattedMessage id="threshold.thresholdLow" defaultMessage="Low" />
                      </Form.Label>
                      <InputNumber
                        name="CriticalLow"
                        step=".1"
                        onInputChange={onChange}
                        customClassName={s.orangeBorder}
                        value={modelThreshold.SelectedThreshold.CriticalLow?.toString() ?? ''}
                        id="formThresholdLowValue"
                      />
                      {localformErrors && localformErrors.CriticalLow && (
                        <p role="alert" className={s.error}>
                          {localformErrors.CriticalLow}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdOpLow">
                      <Form.Label className={s.green}>
                        <FormattedMessage id="threshold.thresholdOpLow" defaultMessage="Op Low" />
                      </Form.Label>
                      <InputNumber
                        name="Low"
                        step=".1"
                        onInputChange={onChange}
                        customClassName={s.greenBorder}
                        value={modelThreshold.SelectedThreshold.Low?.toString() ?? ''}
                        id="formThresholdOpLowValue"
                      />
                      {localformErrors && localformErrors.Low && (
                        <p role="alert" className={s.error}>
                          {localformErrors.Low}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdOpHigh">
                      <Form.Label className={s.green}>
                        <FormattedMessage id="threshold.thresholdOpHigh" defaultMessage="Op High" />
                      </Form.Label>
                      <InputNumber
                        name="High"
                        step=".1"
                        onInputChange={onChange}
                        customClassName={s.greenBorder}
                        value={modelThreshold.SelectedThreshold.High?.toString() ?? ''}
                        id="formThresholdOpHighValue"
                      />
                      {localformErrors && localformErrors.High && (
                        <p role="alert" className={s.error}>
                          {localformErrors.High}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdHigh">
                      <Form.Label className={s.orange}>
                        <FormattedMessage id="threshold.thresholdHigh" defaultMessage="High" />
                      </Form.Label>
                      <InputNumber
                        name="CriticalHigh"
                        step=".1"
                        onInputChange={onChange}
                        customClassName={s.orangeBorder}
                        value={modelThreshold.SelectedThreshold.CriticalHigh?.toString() ?? ''}
                        id="formThresholdHighValue"
                      />
                      {localformErrors && localformErrors.CriticalHigh && (
                        <p role="alert" className={s.error}>
                          {localformErrors.CriticalHigh}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={2}>
                    <Form.Group controlId="formModelVariantThresholdHighHigh">
                      <Form.Label className={s.red}>
                        <FormattedMessage id="threshold.thresholdHighHigh" defaultMessage="High High" />
                      </Form.Label>
                      <InputNumber
                        name="Max"
                        step=".1"
                        onInputChange={onChange}
                        customClassName={s.redBorder}
                        value={modelThreshold.SelectedThreshold.Max?.toString() ?? ''}
                        id="formThresholdHighHighValue"
                      />
                      {localformErrors && localformErrors.Max && (
                        <p role="alert" className={s.error}>
                          {localformErrors.Max}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              <Col lg={2}></Col>
            </Row>
            <Row>
              <Col>
                {localformErrors && localformErrors.thresholdValidation && (
                  <p role="alert" className={s.error}>
                    {localformErrors.thresholdValidation}
                  </p>
                )}
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <Button
                  variant="primary"
                  className={classNames(s.margin5, s.btnSaveChanges)}
                  onClick={submitForm}
                  noValidate
                  data-unittest="saveData"
                >
                  <FormattedMessage id="modelVariantManagement.saveModelThreshold" defaultMessage="SAVE" />
                </Button>
                {modelThreshold.SelectedThreshold.ModelVariantThresholdId > 0 && (
                  <Button
                    variant="outline-secondary"
                    className={classNames(s.btnCancel)}
                    onClick={() => defaultThresholdHandler(modelThreshold.SelectedThreshold.ModelVariantThresholdId)}
                    noValidate
                    data-unittest="saveDefault"
                  >
                    <FormattedMessage id="modelVariantManagement.default" defaultMessage="DEFAULT" />
                  </Button>
                )}
                <Button
                  variant="outline-secondary"
                  className={classNames(s.btnCancel)}
                  onClick={cancelHandler}
                  noValidate
                  data-unittest="saveCancel"
                >
                  <FormattedMessage id="modelVariantManagement.cancelModelVariantThreshold" defaultMessage="CANCEL" />
                </Button>
              </Col>
              <Col lg={8}></Col>
            </Row>
          </div>
        </Form>
      </div>
    </div>
  );
};


ModelThreshold.propTypes = {
  saveModelThreshold: PropTypes.func.isRequired,
  modelThreshold: PropTypes.object.isRequired,

  changeModelThreshold: PropTypes.func.isRequired
};

export default injectIntl(ModelThreshold);
